import { z } from "zod";
import {
  AvailabilityOverrideSchema,
  DateRangeOverrideSchema,
  DaysAvailableOverrideSchema,
  GroupSizePriceOverrideSchema,
  SeasonalAvailabilityOverrideSchema,
} from "./overrideSchemas";

/**
 * Schema for the SubgroupReference model.
 *
 * Represents per-parent overrides for a child sub-group.
 * Stored on the PARENT group's `subgroups[]` array.
 *
 * Overrides are SCOPED — they ONLY affect items within the referenced
 * sub-group when accessed through that specific parent path.
 */

// ── Main SubgroupReference schema ──

export const SubgroupReferenceBaseSchema = z
  .object({
    /** The menuGroupId of the child sub-group */
    subgroupId: z.number().int({ message: "Subgroup ID must be an integer" }).readonly(),
    /** Root/primary group scoping. When set, this override only applies via the specified root group. */
    rootGroupId: z.number().int().optional().readonly(),
    /** Display position within this parent (0-based) */
    position: z.number().int().default(0).readonly(),

    // ── Visibility overrides ──
    isHidden: z.boolean().optional().readonly(),
    displayNameOverride: z.string().optional().readonly(),
    visibleInPOS: z.boolean().optional().readonly(),
    visibleInKiosk: z.boolean().optional().readonly(),
    visibleInOnlineOrdering: z.boolean().optional().readonly(),
    visibleToPartners: z.boolean().optional().readonly(),
    visibleInInvoices: z.boolean().optional().readonly(),

    // ── Service-type availability override ──
    availability: AvailabilityOverrideSchema.optional().readonly(),

    // ── Scheduling overrides ──
    daysAvailable: DaysAvailableOverrideSchema.optional().readonly(),
    dateRange: DateRangeOverrideSchema.optional().readonly(),
    seasonalAvailability: SeasonalAvailabilityOverrideSchema.optional().readonly(),

    // ── Pricing overrides ──
    groupPricingStrategyOverride: z
      .enum(["off", "fixed", "size_based", "menu_specific"])
      .optional()
      .readonly(),
    groupFixedPriceOverride: z.number().min(0).optional().readonly(),
    groupSizePricesOverride: z.array(GroupSizePriceOverrideSchema).optional().readonly(),
  })
  .strict();

// Schema with refinements for validation
const SubgroupReferenceSchema = SubgroupReferenceBaseSchema
  // Fixed price required when strategy is "fixed"
  .refine(
    (data) => {
      if (data.groupPricingStrategyOverride === "fixed") {
        return (
          data.groupFixedPriceOverride !== undefined &&
          data.groupFixedPriceOverride !== null &&
          data.groupFixedPriceOverride >= 0
        );
      }
      return true;
    },
    {
      message:
        "Group fixed price override is required when pricing strategy override is 'fixed'",
      path: ["groupFixedPriceOverride"],
    },
  )
  // Size tiers required when strategy is "size_based"
  .refine(
    (data) => {
      if (data.groupPricingStrategyOverride === "size_based") {
        return (
          data.groupSizePricesOverride &&
          data.groupSizePricesOverride.length > 0
        );
      }
      return true;
    },
    {
      message:
        "At least one size tier is required for size-based group pricing override",
      path: ["groupSizePricesOverride"],
    },
  )
  // Size tier names must be unique
  .refine(
    (data) => {
      if (
        data.groupPricingStrategyOverride === "size_based" &&
        data.groupSizePricesOverride &&
        data.groupSizePricesOverride.length > 1
      ) {
        const names = data.groupSizePricesOverride.map((s) =>
          s.name.toLowerCase().trim(),
        );
        return new Set(names).size === names.length;
      }
      return true;
    },
    {
      message: "Size tier names must be unique in pricing override",
      path: ["groupSizePricesOverride"],
    },
  );

export type SubgroupReferenceType = z.infer<typeof SubgroupReferenceSchema>;

export default SubgroupReferenceSchema;
